import apiClient from './apiClient';
import type {
  Scooter,
  ScooterUsage,
  ScooterFilters,
} from './types';

// Backend's actual PaginatedResponse format
interface BackendPaginatedResponse<T> {
  items: T[];
  total: number;
  offset: number;
  limit: number;
  has_more: boolean;
}

const SCOOTER_PAGE_SIZE = 100;

// The signed-in user comes from the auth service (ID token claims); the
// backend's GET /api/v1/auth/me is a whoami/token diagnostic and the rider app
// does not call it.
class ScooterService {
  /**
   * Get user's scooters with enhanced UI properties
   */
  async getUserScooters(filters?: ScooterFilters): Promise<Scooter[]> {
    console.log('🔍 ScooterService: Getting user scooters with filters:', filters);
    
    // Fetch every page: the endpoint defaults to 20 per page, so fleets with
    // more scooters were cut off. 100 is the backend's maximum page size.
    let scooters: Scooter[] = [];
    let offset = 0;
    for (;;) {
      const response = await apiClient.get<BackendPaginatedResponse<Scooter>>(
        `/api/v1/scooters/?limit=${SCOOTER_PAGE_SIZE}&offset=${offset}`
      );
      const page = response.data.items || [];
      scooters = scooters.concat(page);
      if (!response.data.has_more || page.length === 0) break;
      offset += page.length;
    }
    console.log('🔍 ScooterService: Raw scooters from API:', scooters);
    
    // Enhance scooters for UI (bind this context)
    scooters = scooters.map((scooter: Scooter) => this.enhanceScooterForUI(scooter));
    console.log('🔍 ScooterService: Enhanced scooters:', scooters);
    
    // Apply filters if provided
    if (filters?.status) {
      console.log('🔍 ScooterService: Applying status filter:', filters.status);
      scooters = scooters.filter((s: Scooter) => s.status === filters.status);
    }
    
    console.log('🔍 ScooterService: Final scooters list:', scooters);
    return scooters;
  }

  /**
   * Get specific scooter by VIN
   */
  async getScooterByVin(vin: string): Promise<Scooter> {
    const scooters = await this.getUserScooters();
    const scooter = scooters.find(s => s.vin === vin);
    
    if (!scooter) {
      throw new Error(`Scooter with VIN ${vin} not found`);
    }
    
    return scooter;
  }

  /**
   * Get cumulative usage for a scooter
   */
  async getScooterUsage(vin: string): Promise<ScooterUsage> {
    const response = await apiClient.get<ScooterUsage>(
      `/api/v1/scooters/${vin}/usage`
    );
    
    // Direct response from backend
    return response.data;
  }

  /**
   * Update scooter display preferences (nickname, color)
   */
  async updateScooterPreferences(
    vin: string, 
    preferences: { nickname?: string; color_code?: string }
  ): Promise<Scooter> {
    const response = await apiClient.put<Scooter>(
      `/api/v1/scooters/${vin}/preferences`,
      preferences
    );
    
    // Direct response from backend
    return this.enhanceScooterForUI(response.data);
  }

  /**
   * Check if scooter is blacklisted (mock implementation)
   */
  async checkBlacklistStatus(vin: string): Promise<{
    is_blacklisted: boolean;
    reason?: string;
    contact_support?: boolean;
  }> {
    console.log('🔍 ScooterService: Checking blacklist status for VIN:', vin);

    try {
      // Check the debt endpoint — pending debt means the scooter was
      // blacklisted due to pack depletion.
      const response = await apiClient.get<any>(`/api/v1/packs/debt/${vin}`);
      const debt = response.data;
      const energyDebtAh = parseFloat(debt.energy_debt_ah ?? '0');
      const scooterDebtKm = parseFloat(debt.scooter_debt_km ?? '0');
      const isBlacklisted = debt.status === 'pending' && (energyDebtAh > 0 || scooterDebtKm > 0);
      const reasons = [
        ...(energyDebtAh > 0 ? [`${energyDebtAh.toFixed(1)} Ah of previous usage will be rolled over to the next pack(s)`] : []),
        ...(scooterDebtKm > 0 ? [`${scooterDebtKm.toFixed(1)} km of previous usage will be rolled over to the next pack(s)`] : []),
      ];

      console.log('🔍 ScooterService: Blacklist status from debt check:', isBlacklisted);
      return {
        is_blacklisted: isBlacklisted,
        reason: isBlacklisted ? reasons.join(' / ') : undefined,
        contact_support: false,
      };
    } catch (error) {
      console.warn('🔍 ScooterService: Failed to check blacklist status, assuming not blacklisted:', error);
      return { is_blacklisted: false };
    }
  }

  /**
   * Enhance scooter object with UI-specific properties
   */
  private enhanceScooterForUI(scooter: Scooter): Scooter {
    // Handle null/undefined VIN gracefully
    const vin = scooter.vin || '';
    const vinLast4 = vin.length >= 4 ? vin.slice(-4) : vin;
    
    // Handle null/undefined model gracefully
    const model = scooter.model || 'Unknown Model';
    
    // Generate display name (last 4 VIN digits + model)
    const display_name = vinLast4 ? `${vinLast4} • ${model}` : model;
    
    // Generate color code for UI identification (handle null/undefined color)
    // Backend returns 'color' field, frontend uses 'color_code'
    const color_code = scooter.color_code || scooter.color || this.generateScooterColor(vin);
    
    // Calculate cumulative Ah used (placeholder - should come from backend)
    const cumulative_ah_used = scooter.cumulative_ah_used || 0;
    
    return {
      ...scooter,
      // Ensure all required fields have fallback values
      vin: vin,
      model: model,
      status: scooter.status || 'active',
      display_name,
      color_code,
      cumulative_ah_used,
      // Handle optional date fields
      registration_date: scooter.registration_date || undefined,
      last_maintenance: scooter.last_maintenance || undefined,
      purchase_date: scooter.purchase_date || undefined,
      warranty_end_date: scooter.warranty_end_date || undefined,
      // Handle optional numeric fields
      total_mileage: scooter.total_mileage || 0,
    };
  }

  /**
   * Generate consistent color for scooter based on VIN
   */
  private generateScooterColor(vin: string): string {
    const colors = [
      '#1677ff', // Blue
      '#52c41a', // Green
      '#faad14', // Gold
      '#f5222d', // Red
      '#722ed1', // Purple
      '#fa8c16', // Orange
      '#13c2c2', // Cyan
      '#eb2f96', // Magenta
    ];
    
    // Handle null/undefined/empty VIN
    if (!vin || vin.length === 0) {
      return colors[0]; // Default to blue
    }
    
    // Use VIN hash to consistently assign color
    let hash = 0;
    for (let i = 0; i < vin.length; i++) {
      hash = vin.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  }

  /**
   * Get scooter recommendations for pack purchase
   */
  async getScooterPackRecommendations(vin: string): Promise<{
    urgent_renewal: boolean;
    recommended_pack_types: string[];
    estimated_duration: number; // days
  }> {
    const response = await apiClient.get<{
      urgent_renewal: boolean;
      recommended_pack_types: string[];
      estimated_duration: number;
    }>(
      `/api/v1/scooters/${vin}/pack-recommendations`
    );
    
    // Direct response from backend
    return response.data;
  }
}

export const scooterService = new ScooterService();
export default scooterService;
